import { getNames as getMonthNames } from "../period/Month.js";
import { modulo } from "@foretagsplatsen/core-helpers/number";
import { shift } from "../../core/helpers/date-util.js";
import Model from "../Model.js";

/**
 * Represent a month (i.e., a column) in the forecast editor. The
 * difference with `Month` is that `MonthPeriod` isn't attached to any
 * fiscal year.
 *
 * @param {int} year - Represent the calendar year this month is in,
 *   e.g., 2022
 * @param {int} index - Waiting for the backend team to tell us what
 *   this means exactly
 * @param {int} calendarIndex - The numeric version of the month,
 *   e.g., 1 for January and 12 for December
 * @param {FiscalYear} [fiscalYear] - The fiscal year this month is in
 * @param {boolean} [isClosing] - True when this month is the closing month
 *   of its fiscal year
 */
export default class MonthPeriod extends Model {
	constructor({
		year,
		index,
		calendarIndex,
		fiscalYear,
		isClosing = false,
	} = {}) {
		super(...arguments);

		this._year = year;
		this._index = index;
		this._calendarIndex = calendarIndex;
		this._fiscalYear = fiscalYear;
		this._isClosing = isClosing;
	}

	setIsClosing(bool) {
		this._isClosing = bool;
	}

	isClosing() {
		return this._isClosing;
	}

	getYear() {
		return this._year;
	}

	setYear(year) {
		this._year = year;
	}

	getIndex() {
		return this._index;
	}

	setIndex(index) {
		this._index = index;
	}

	getCalendarIndex() {
		return this._calendarIndex;
	}

	setCalendarIndex(calendarIndex) {
		this._calendarIndex = calendarIndex;
	}

	getFiscalYear() {
		return this._fiscalYear;
	}

	asDate() {
		return new Date(this._year, this._calendarIndex - 1);
	}

	asPeriod() {
		return this._fiscalYear.findPeriod("Month", this._index);
	}

	asString() {
		return `${getMonthNames()[this._calendarIndex - 1]} ${this._year}`;
	}

	equal(object) {
		return (
			object instanceof this.constructor &&
			object.getYear() === this.getYear() &&
			object.getIndex() === this.getIndex() &&
			object.getCalendarIndex() === this.getCalendarIndex()
		);
	}

	isOlderThan(object) {
		if (!(object instanceof this.constructor)) {
			return false;
		}

		if (object.getYear() !== this.getYear()) {
			return object.getYear() > this.getYear();
		}

		return object.getCalendarIndex() > this.getCalendarIndex();
	}

	compareTo(object) {
		if (this.equal(object)) {
			return 0;
		} else if (this.isOlderThan(object)) {
			return -1;
		}
		return 1;
	}

	max(monthPeriod) {
		return this.isOlderThan(monthPeriod) ? monthPeriod : this;
	}

	add(numberOfPeriods) {
		const date = shift({
			date: this.asDate(),
			amount: numberOfPeriods,
			periodType: "month",
		});

		return new MonthPeriod({
			year: date.getFullYear(),
			index: 1 + modulo(this._index - 1 + numberOfPeriods, 12),
			calendarIndex: date.getMonth() + 1,
		});
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.year = this._year;
		properties.index = this._index;
		properties.calendarIndex = this._calendarIndex;
		properties.type = this.constructor.getType();

		properties.fiscalYearReference =
			this._fiscalYear?.asFiscalYearReference();

		return properties;
	}

	static transformMaterializationData(data, context) {
		data.fiscalYear =
			data.fiscalYearReference?.fiscalYearId &&
			context.getFiscalYearById(data.fiscalYearReference.fiscalYearId);
	}

	static getType() {
		return "Month";
	}

	static fromMonth(month) {
		return new this({
			year: month.getStart().getFullYear(),
			index: month.getPeriodIndex(),
			calendarIndex: month.getCalendarIndex(),
			fiscalYear: month.getYear(),
		});
	}
}
